import { interpolatingMedian } from "./utils/median.js";
import { buildObjectPoints, meanEdgePx, rotateVectorByQuaternion, validateQuad } from "./ar/qr/qr-pose.js";
import { t as geodesicAngleRad } from "./geodesic-angle-CMHpPv_b.js";
import { n as aggregateQrPose } from "./qr-pose-aggregation-Ce3agYE2.js";
import { PlanarPnpSquare, homographyFromCorrespondences, realIppeCandidates, solveLinear } from "./ar/qr/planar-pnp.js";
//#region ../src/ar/qr/qr-multi-view-pose.ts
const DEFAULT_ROBUST_SCALE_PX = 1;
const DEFAULT_MAX_ITERATIONS = 30;
const DEFAULT_MAX_STARTS = 3;
/** Starts closer than this to an earlier one are dropped. */
const DUPLICATE_START_DEG = 2;
const JACOBIAN_STEP_RAD = 1e-6;
/** Converged when the step is below this (6e-7 deg) ... */
const STEP_DONE_RAD = 1e-8;
/** ... or the robust cost fell by less than this fraction. */
const COST_DONE_RELATIVE = 1e-9;
const RAD_TO_DEG = 180 / Math.PI;
/** How far a camera quaternion's norm may be from 1 (Float32 input is ~1e-7). */
const UNIT_QUATERNION_TOLERANCE = .001;
function qmul(a, b) {
	return [
		a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
		a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
		a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
		a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2]
	];
}
function qconj(q) {
	return [
		-q[0],
		-q[1],
		-q[2],
		q[3]
	];
}
function qnormalize(q) {
	const n = Math.hypot(q[0], q[1], q[2], q[3]);
	return [
		q[0] / n,
		q[1] / n,
		q[2] / n,
		q[3] / n
	];
}
/** A rotation vector (axis * angle, rad) as a unit quaternion. */
function qexp(v) {
	const angle = Math.hypot(v[0], v[1], v[2]);
	if (angle < 1e-12) return qnormalize([
		v[0] / 2,
		v[1] / 2,
		v[2] / 2,
		1
	]);
	const s = Math.sin(angle / 2) / angle;
	return [
		v[0] * s,
		v[1] * s,
		v[2] * s,
		Math.cos(angle / 2)
	];
}
function angleBetweenDeg(a, b) {
	const dot = Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3]);
	return 2 * Math.acos(Math.min(1, dot)) * RAD_TO_DEG;
}
/** A row-major rotation matrix as a unit quaternion (Shepperd's method). */
function quatFromMatrix(m) {
	const trace = m[0] + m[4] + m[8];
	if (trace > 0) {
		const s = 2 * Math.sqrt(trace + 1);
		return qnormalize([
			(m[7] - m[5]) / s,
			(m[2] - m[6]) / s,
			(m[3] - m[1]) / s,
			s / 4
		]);
	}
	if (m[0] > m[4] && m[0] > m[8]) {
		const s = 2 * Math.sqrt(1 + m[0] - m[4] - m[8]);
		return qnormalize([
			s / 4,
			(m[1] + m[3]) / s,
			(m[2] + m[6]) / s,
			(m[7] - m[5]) / s
		]);
	}
	if (m[4] > m[8]) {
		const s = 2 * Math.sqrt(1 + m[4] - m[0] - m[8]);
		return qnormalize([
			(m[1] + m[3]) / s,
			s / 4,
			(m[5] + m[7]) / s,
			(m[2] - m[6]) / s
		]);
	}
	const s = 2 * Math.sqrt(1 + m[8] - m[0] - m[4]);
	return qnormalize([
		(m[2] + m[6]) / s,
		(m[5] + m[7]) / s,
		s / 4,
		(m[3] - m[1]) / s
	]);
}
/**
* A finite pose whose rotation is a UNIT quaternion: the projection assumes
* one, and a norm of 0.9 already moves the result by millimetres and the
* corners by most of a pixel (review 2026-09-24).
*/
function isFinitePose(pose) {
	return pose.position.length === 3 && pose.rotation.length === 4 && [...pose.position, ...pose.rotation].every(Number.isFinite) && Math.abs(Math.hypot(...pose.rotation) - 1) <= UNIT_QUATERNION_TOLERANCE;
}
function isUsableView(view) {
	const { fx, fy, cx, cy } = view.intrinsics;
	return fx > 0 && fy > 0 && Number.isFinite(fx) && Number.isFinite(fy) && Number.isFinite(cx) && Number.isFinite(cy) && isFinitePose(view.cameraPose) && validateQuad(view.corners).ok;
}
/**
* `view`'s own code position and its REAL IPPE candidates' world rotations.
*
* The position is the single-frame solve's (lowest reprojection error over
* ALL candidates), not the real root's: measured over noise, distance and
* SLAM error, the real root's position was never better and at close range
* worse (p95 1.55 vs 1.26 deg at 0.8 m, 0.5 px; plan §12).
*/
function seedOf(view, sizeM) {
	const { fx, fy, cx, cy } = view.intrinsics;
	const object = buildObjectPoints(sizeM);
	const H = homographyFromCorrespondences(object.map((o) => [o[0], o[1]]), view.corners.map((c) => [(c.x - cx) / fx, (c.y - cy) / fy]));
	const own = new PlanarPnpSquare().solve(object, view.corners, view.intrinsics);
	if (!H || !own) return null;
	const candidates = realIppeCandidates(H);
	if (candidates.length === 0) return null;
	const cam = view.cameraPose;
	const inCamera = [
		own.tvec[0],
		-own.tvec[1],
		-own.tvec[2]
	];
	const offset = rotateVectorByQuaternion(cam.rotation, inCamera);
	return {
		position: [
			cam.position[0] + offset[0],
			cam.position[1] + offset[1],
			cam.position[2] + offset[2]
		],
		rotations: candidates.map(({ R }) => qnormalize(qmul(cam.rotation, quatFromMatrix([
			R[0],
			R[1],
			R[2],
			-R[3],
			-R[4],
			-R[5],
			-R[6],
			-R[7],
			-R[8]
		]))))
	};
}
/** Corner residuals (x, y per corner, all views) for rotation `q`; null if a corner falls behind a camera. */
function residuals(pr, q) {
	const out = [];
	for (let i = 0; i < pr.views.length; i++) {
		const { cameraPose: cam, intrinsics, corners } = pr.views[i];
		const p = pr.positions[i];
		const camInv = qconj(cam.rotation);
		for (let k = 0; k < 4; k++) {
			const w = rotateVectorByQuaternion(q, pr.object[k]);
			const [x, y, z] = rotateVectorByQuaternion(camInv, [
				w[0] + p[0] - cam.position[0],
				w[1] + p[1] - cam.position[1],
				w[2] + p[2] - cam.position[2]
			]);
			const depth = -z;
			if (!(depth > 1e-6)) return null;
			out.push(intrinsics.cx + intrinsics.fx * x / depth - corners[k].x, intrinsics.cy - intrinsics.fy * y / depth - corners[k].y);
		}
	}
	return out;
}
/** Per-corner robust weights (repeated for x and y) and the robust cost. */
function robustWeights(r, scale) {
	const w = [];
	let cost = 0;
	for (let j = 0; j < r.length; j += 2) {
		const e = Math.hypot(r[j], r[j + 1]);
		const inlier = e <= scale;
		const wj = inlier ? 1 : scale / e;
		cost += inlier ? e * e : 2 * scale * e - scale * scale;
		w.push(wj, wj);
	}
	return {
		w,
		cost
	};
}
function rmsPx(r) {
	let s = 0;
	for (const v of r) s += v * v;
	return Math.sqrt(s / (r.length / 2));
}
/** Weighted normal equations J^T W J (row-major 3x3) and -J^T W r at `q`. */
function normalEquations(pr, q, r, w) {
	const J = [];
	for (let c = 0; c < 3; c++) {
		const d = [
			0,
			0,
			0
		];
		d[c] = JACOBIAN_STEP_RAD;
		const rp = residuals(pr, qmul(q, qexp(d)));
		d[c] = -JACOBIAN_STEP_RAD;
		const rm = residuals(pr, qmul(q, qexp(d)));
		if (!rp || !rm) return null;
		J.push(rp.map((v, j) => (v - rm[j]) / (2 * JACOBIAN_STEP_RAD)));
	}
	const A = new Array(9).fill(0);
	const b = [
		0,
		0,
		0
	];
	for (let j = 0; j < r.length; j++) for (let a = 0; a < 3; a++) {
		b[a] -= w[j] * J[a][j] * r[j];
		for (let c = 0; c < 3; c++) A[a * 3 + c] += w[j] * J[a][j] * J[c][j];
	}
	return {
		A,
		b
	};
}
/**
* One damped Gauss-Newton iteration: raise the damping until a step lowers
* the robust cost. `null` when no step does (converged or stuck).
*/
function dampedIteration(pr, state, lambda) {
	const eq = normalEquations(pr, state.q, state.r, robustWeights(state.r, pr.robustScalePx).w);
	if (!eq) return null;
	let damping = lambda;
	for (let tries = 0; tries < 8; tries++, damping *= 5) {
		const dx = solveLinear(eq.A.map((v, idx) => idx % 4 === 0 ? v + damping * (v + 1e-12) : v), eq.b);
		if (!dx) return null;
		const q = qnormalize(qmul(state.q, qexp(dx)));
		const r = residuals(pr, q);
		if (!r) continue;
		const cost = robustWeights(r, pr.robustScalePx).cost;
		if (cost > state.cost) continue;
		const done = Math.hypot(...dx) < STEP_DONE_RAD || state.cost - cost <= COST_DONE_RELATIVE * state.cost;
		return {
			state: {
				q,
				r,
				cost,
				iterations: state.iterations + 1
			},
			lambda: Math.max(damping / 3, 1e-9),
			done
		};
	}
	return null;
}
function refine(pr, start, maxIterations) {
	const r = residuals(pr, start);
	if (!r) return null;
	let state = {
		q: start,
		r,
		cost: robustWeights(r, pr.robustScalePx).cost,
		iterations: 0
	};
	let lambda = .001;
	for (let it = 0; it < maxIterations; it++) {
		const step = dampedIteration(pr, state, lambda);
		if (!step) break;
		state = step.state;
		lambda = step.lambda;
		if (step.done) break;
	}
	return state;
}
/**
* The tilt's 1-sigma (1 px corner noise), degrees, along its worst direction:
* the larger eigenvalue of the in-plane-axes block of (J^T W J)^-1. The
* perturbation is code-local, so its x and y components tilt the normal.
*/
function tiltSigmaDeg(pr, state) {
	const eq = normalEquations(pr, state.q, state.r, robustWeights(state.r, pr.robustScalePx).w);
	if (!eq) return Infinity;
	const col = (c) => solveLinear(eq.A, c === 0 ? [
		1,
		0,
		0
	] : [
		0,
		1,
		0
	]);
	const c0 = col(0);
	const c1 = col(1);
	if (!c0 || !c1) return Infinity;
	const [a, b, d] = [
		c0[0],
		c0[1],
		c1[1]
	];
	const maxEig = (a + d) / 2 + Math.hypot((a - d) / 2, b);
	return maxEig >= 0 ? Math.sqrt(maxEig) * RAD_TO_DEG : Infinity;
}
/**
* The distinct candidate rotations, cheapest first by their robust cost over
* ALL views, at most `maxStarts` of them. A view's mirror flip fits the other
* views badly, so it ranks low as soon as the views differ.
*/
function rankedStarts(pr, seeds, maxStarts) {
	const distinct = [];
	for (const q of seeds.flatMap((s) => s.rotations)) if (!distinct.some((s) => angleBetweenDeg(s, q) < DUPLICATE_START_DEG)) distinct.push(q);
	const costOf = (q) => {
		const r = residuals(pr, q);
		return r ? robustWeights(r, pr.robustScalePx).cost : Infinity;
	};
	return distinct.map((q) => ({
		q,
		cost: costOf(q)
	})).filter((s) => Number.isFinite(s.cost)).sort((a, b) => a.cost - b.cost).slice(0, maxStarts).map((s) => s.q);
}
/**
* The usable views with their seeds; an unusable view (see
* `isUsableView`, or no single-frame solve / real candidate) is dropped.
*/
function usableSeeds(views, sizeM) {
	const used = [];
	const seeds = [];
	for (const view of views) {
		const seed = isUsableView(view) ? seedOf(view, sizeM) : null;
		if (!seed) continue;
		used.push(view);
		seeds.push(seed);
	}
	return {
		views: used,
		seeds
	};
}
/** Whether the size and the options are in range (NaN fails every check). */
function inRange(sizeM, robustScalePx, maxStarts, maxIterations) {
	return sizeM > 0 && Number.isFinite(sizeM) && robustScalePx > 0 && maxStarts >= 1 && maxIterations >= 1;
}
/** Each view's own RMS corner error from the stacked residuals (8 per view). */
function perViewRmsPx(r) {
	const out = [];
	for (let i = 0; i < r.length; i += 8) out.push(rmsPx(r.slice(i, i + 8)));
	return out;
}
/** The lowest-cost refinement over `starts`, and the iterations it all took. */
function bestRefinement(pr, starts, maxIterations) {
	let best = null;
	let iterations = 0;
	for (const start of starts) {
		const state = refine(pr, start, maxIterations);
		if (!state) continue;
		iterations += state.iterations;
		if (!best || state.cost < best.cost) best = state;
	}
	return {
		best,
		iterations
	};
}
/**
* The code's world rotation from several views of it, with the mean of the
* used views' own positions. An unusable view (not four finite corners in
* front-facing winding, bad intrinsics, a non-finite or non-unit camera
* pose, no single-frame solve or real candidate) is DROPPED and counted in
* `droppedViews`. `null` when no usable view is left, `sizeM` is not a
* positive finite number, an option is out of range, or no start converges.
*/
function solveQrPoseMultiView(views, sizeM, options = {}) {
	const { robustScalePx = DEFAULT_ROBUST_SCALE_PX, maxIterations = DEFAULT_MAX_ITERATIONS, maxStarts = DEFAULT_MAX_STARTS } = options;
	if (!inRange(sizeM, robustScalePx, maxStarts, maxIterations)) return null;
	const { views: used, seeds } = usableSeeds(views, sizeM);
	if (used.length === 0) return null;
	const pr = {
		views: used,
		object: buildObjectPoints(sizeM),
		positions: seeds.map((seed) => seed.position),
		robustScalePx
	};
	const starts = rankedStarts(pr, seeds, maxStarts);
	const { best, iterations } = bestRefinement(pr, starts, maxIterations);
	if (!best) return null;
	const mean = (a) => seeds.reduce((sum, seed) => sum + seed.position[a], 0) / seeds.length;
	return {
		rotation: best.q,
		position: [
			mean(0),
			mean(1),
			mean(2)
		],
		costPx: rmsPx(best.r),
		viewRmsPx: perViewRmsPx(best.r),
		viewPositions: seeds.map((seed) => seed.position),
		views: used.length,
		droppedViews: views.length - used.length,
		tiltSigmaDeg: tiltSigmaDeg(pr, best),
		starts: starts.length,
		iterations
	};
}
/**
* One view's RMS corner error, px, if the code had world rotation
* `rotation` and the view's own single-frame position - the motion
* detector's "does the newest view fit the others' rotation" (plan §26).
* `null` when the view is unusable or the size invalid.
*/
function viewErrorAtRotationPx(view, rotation, sizeM) {
	if (!(sizeM > 0) || !Number.isFinite(sizeM) || !isUsableView(view)) return null;
	const seed = seedOf(view, sizeM);
	if (!seed) return null;
	const r = residuals({
		views: [view],
		object: buildObjectPoints(sizeM),
		positions: [seed.position],
		robustScalePx: DEFAULT_ROBUST_SCALE_PX
	}, qnormalize(rotation));
	return r ? rmsPx(r) : null;
}
//#endregion
//#region ../src/ar/qr/qr-fused-window.ts
const FUSED_WINDOW_DEFAULTS = {
	windowSize: 8,
	gapMs: 4e3,
	radiusM: Infinity,
	sinceMs: -Infinity
};
/**
* The window options with every value checked: a missing, NaN or
* out-of-range value takes its default instead of silently switching a
* check off (an explicit `undefined` from a plain JS caller included).
*/
function resolveFusedWindowOptions(options) {
	const d = FUSED_WINDOW_DEFAULTS;
	const pick = (v, fallback, ok) => typeof v === "number" && ok(v) ? v : fallback;
	return {
		windowSize: pick(options.windowSize, d.windowSize, (x) => x >= 1),
		gapMs: pick(options.gapMs, d.gapMs, (x) => x >= 0),
		radiusM: pick(options.radiusM, d.radiusM, (x) => x > 0),
		sinceMs: pick(options.sinceMs, d.sinceMs, (x) => !Number.isNaN(x))
	};
}
function distance$1(a, b) {
	return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}
/** Where the run ending at the newest entry starts (non-empty input): same epoch, no step over `gapMs`. */
function runStart(entries, gapMs) {
	const newest = entries[entries.length - 1];
	let i = entries.length - 1;
	while (i > 0 && !breaksWindow(entries[i - 1], entries[i], newest, gapMs)) i--;
	return i;
}
/**
* The entries without the `native` ones of the run ending at the newest
* entry, when that run holds a `finder` or `memory` entry (QR near-frontal
* pose plan §54-§55): once a code's corner order is known, its detector-
* order frames are 90/180 deg wrong whenever the code is rolled past 45 deg
* in the image, and a majority of them agrees with itself. Entries without
* an order source, an all-native run, and anything before a gap or in
* another epoch are kept. The same array comes back when nothing is
* dropped.
*/
function ignoreNativeWhenOrdered(entries, gapMs) {
	if (entries.length === 0) return entries;
	const start = runStart(entries, gapMs);
	let ordered = false;
	let native = false;
	for (let i = start; i < entries.length; i++) {
		const source = entries[i].orderSource;
		if (source === "native") native = true;
		else if (source === "finder" || source === "memory") ordered = true;
	}
	if (!ordered || !native) return entries;
	return entries.filter((e, i) => i < start || e.orderSource !== "native");
}
/**
* The window (oldest to newest): walking back from the newest entry, stop at
* another frame epoch, at a step in time larger than `gapMs` (either
* direction; a NaN step breaks too) or at an entry older than `sinceMs`;
* leave out entries farther than `radiusM` from the newest raw position in
* the run (entries without a raw pose are kept); keep at most `windowSize`.
* Native entries of an ordered run are ignored first
* ({@link ignoreNativeWhenOrdered}).
*/
function selectFusedWindow(entries, options = {}) {
	const o = resolveFusedWindowOptions(options);
	const list = ignoreNativeWhenOrdered(entries, o.gapMs);
	const newest = list[list.length - 1];
	if (!newest) return [];
	const run = [];
	for (let i = list.length - 1; i >= 0; i--) {
		const e = list[i];
		if (breaksWindow(e, list[i + 1], newest, o.gapMs)) break;
		if (e.timestamp < o.sinceMs) break;
		run.push(e);
	}
	const anchor = run.find((e) => e.rawPose)?.rawPose?.position;
	return (anchor ? run.filter((e) => !e.rawPose || distance$1(e.rawPose.position, anchor) <= o.radiusM) : run).slice(0, o.windowSize).reverse();
}
/** Another frame epoch than the newest, or a time step to the next entry above `gapMs` (or NaN). */
function breaksWindow(e, later, newest, gapMs) {
	if ((e.frameEpoch ?? 0) !== (newest.frameEpoch ?? 0)) return true;
	return later !== void 0 && !(Math.abs(later.timestamp - e.timestamp) <= gapMs);
}
//#endregion
//#region ../src/ar/qr/qr-motion.ts
const DEFAULTS$1 = {
	motionWindow: 4,
	moveM: .03,
	turnPx: 3,
	persistence: 4,
	sizeM: .16,
	gapMs: FUSED_WINDOW_DEFAULTS.gapMs
};
function resolve(o) {
	const pick = (v, d, ok) => typeof v === "number" && ok(v) ? v : d;
	return {
		motionWindow: pick(o.motionWindow, DEFAULTS$1.motionWindow, (x) => x >= 2),
		moveM: pick(o.moveM, DEFAULTS$1.moveM, (x) => x > 0),
		turnPx: pick(o.turnPx, DEFAULTS$1.turnPx, (x) => x > 0),
		persistence: pick(o.persistence, DEFAULTS$1.persistence, (x) => x >= 1),
		sizeM: pick(o.sizeM, DEFAULTS$1.sizeM, (x) => x > 0 && Number.isFinite(x)),
		gapMs: pick(o.gapMs, DEFAULTS$1.gapMs, (x) => x >= 0),
		solve: o.solve ?? solveQrPoseMultiView
	};
}
const NONE = {
	movingCandidate: false,
	turningCandidate: false,
	offsetM: null,
	speedMps: null,
	newestFitPx: null,
	turnRateDegPerS: null,
	newestEdgePx: null
};
function medianPosition(ps) {
	const axis = (a) => interpolatingMedian(ps.map((p) => p[a]));
	return [
		axis(0),
		axis(1),
		axis(2)
	];
}
function distance(a, b) {
	return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}
/**
* Each view's own position: the producer's raw poses when every entry has
* one - solved at the size the producer measured - else the joint solve's
* per-view positions at the assumed `sizeM`. A wrong size pulls each view's
* position toward its camera, so a walking camera would move a still code.
*/
function viewPositions(window, joint) {
	const raws = window.map((e) => e.rawPose?.position);
	return raws.every((p) => p !== void 0) ? raws : joint.viewPositions;
}
/**
* Seconds from the rest's median detection to the newest: the time base of
* both rates, since the newest is compared with the rest as a whole.
*/
function sinceRestS(times) {
	return (times[times.length - 1] - interpolatingMedian(times.slice(0, -1))) / 1e3;
}
/** The newest view's own position offset and its speed. */
function translation(ps, dtS) {
	const newest = ps[ps.length - 1];
	const offsetM = distance(newest, medianPosition(ps.slice(0, -1)));
	return {
		offsetM,
		speedMps: dtS > 0 ? offsetM / dtS : null
	};
}
/**
* Rough turn rate: the newest single-frame (raw) rotation against the
* others' joint rotation. The raw rotation is the flip-prone one near
* head-on, so this is for display, never for a decision.
*/
function turnRate(newest, restRotation, dtS) {
	const raw = newest.rawPose;
	if (!raw || !(dtS > 0)) return null;
	return geodesicAngleRad(raw.rotation, restRotation) * 180 / Math.PI / dtS;
}
function toView(e) {
	return {
		corners: e.corners,
		cameraPose: e.cameraPose,
		intrinsics: e.intrinsics
	};
}
/**
* One detection's motion signals: the newest of the last `motionWindow`
* detections (one frame epoch, no long gap) against the rest. The newest
* against the rest, not a spread over the window, so a single outlier frame
* is a candidate for ONE detection only and can never reach the persistence.
*/
function measureQrMotion(entries, options = {}) {
	const o = resolve(options);
	const window = selectFusedWindow(entries, {
		windowSize: o.motionWindow,
		gapMs: o.gapMs
	});
	const newest = window[window.length - 1];
	const newestEdgePx = newest ? meanEdgePx(newest.corners) : null;
	if (!newest || window.length < 2) return {
		...NONE,
		newestEdgePx
	};
	const all = o.solve(window.map(toView), o.sizeM);
	const rest = o.solve(window.slice(0, -1).map(toView), o.sizeM);
	if (!all || !rest || all.droppedViews > 0) return {
		...NONE,
		newestEdgePx
	};
	const dtS = sinceRestS(window.map((e) => e.timestamp));
	const { offsetM, speedMps } = translation(viewPositions(window, all), dtS);
	const newestFitPx = viewErrorAtRotationPx(toView(newest), rest.rotation, o.sizeM);
	return {
		movingCandidate: offsetM > o.moveM,
		turningCandidate: newestFitPx !== null && newestFitPx > o.turnPx,
		offsetM,
		speedMps,
		newestFitPx,
		turnRateDegPerS: turnRate(newest, rest.rotation, dtS),
		newestEdgePx
	};
}
function step(flag, candidate, atMs, persistence) {
	if (candidate === flag.value) return {
		...flag,
		run: 0,
		runStartMs: null
	};
	const run = flag.run + 1;
	const runStartMs = flag.runStartMs ?? atMs;
	if (run < persistence) return {
		...flag,
		run,
		runStartMs
	};
	return {
		value: candidate,
		run: 0,
		runStartMs: null,
		stillSinceMs: candidate ? flag.stillSinceMs : runStartMs
	};
}
/** The same detection: the store passes the corners through by reference. */
function sameDetection(a, b) {
	return a === b || a.corners === b.corners && a.timestamp === b.timestamp;
}
function stateOf(moving, turning) {
	if (moving && turning) return "moving+turning";
	if (moving) return "moving";
	return turning ? "turning" : "still";
}
/**
* The per-code detector with persistence: moving and turning are each
* confirmed only after `persistence` consecutive detections agree, in both
* directions. A new frame epoch starts it afresh. Native entries of an
* ordered run are ignored (`ignoreNativeWhenOrdered`).
*/
function createQrMotionTracker(options = {}) {
	const o = resolve(options);
	const fresh = () => ({
		value: false,
		run: 0,
		runStartMs: null,
		stillSinceMs: null
	});
	let moving = fresh();
	let turning = fresh();
	let epoch = null;
	let lastNewest = null;
	let last = null;
	const restart = (e) => {
		moving = fresh();
		turning = fresh();
		epoch = e;
	};
	const result = (signals) => {
		const state = stateOf(moving.value, turning.value);
		const since = [moving.stillSinceMs, turning.stillSinceMs].filter((t) => t !== null);
		return {
			...signals,
			state,
			moving: moving.value,
			turning: turning.value,
			stillSinceMs: state === "still" && since.length ? Math.max(...since) : null
		};
	};
	const isReRead = (newest) => last !== null && lastNewest !== null && sameDetection(newest, lastNewest);
	const startsNewRun = (newest) => epoch !== (newest.frameEpoch ?? 0) || lastNewest !== null && newest.timestamp < lastNewest.timestamp;
	const advance = (signals, atMs) => {
		moving = step(moving, signals.movingCandidate, atMs, o.persistence);
		turning = step(turning, signals.turningCandidate, atMs, o.persistence);
	};
	return {
		update(input) {
			const entries = ignoreNativeWhenOrdered(input, o.gapMs);
			const newest = entries[entries.length - 1];
			if (newest && isReRead(newest)) return last;
			const signals = measureQrMotion(entries, options);
			if (!newest) return {
				...signals,
				state: "still",
				moving: false,
				turning: false,
				stillSinceMs: null
			};
			if (startsNewRun(newest)) restart(newest.frameEpoch ?? 0);
			lastNewest = newest;
			if (signals.offsetM !== null) advance(signals, newest.timestamp);
			last = result(signals);
			return last;
		},
		reset() {
			moving = fresh();
			turning = fresh();
			epoch = null;
			lastNewest = null;
			last = null;
		}
	};
}
//#endregion
//#region ../src/ar/qr/qr-fused-pose.ts
/**
* The fused QR pose over a window of detections (QR near-frontal pose plan
* 2026-09-23-2314, M3b b1, §19): the joint rotation over the window
* (`qr-fused-window.ts`) (`solveQrPoseMultiView`), the joint-fit gate that
* decides when the pose may be used (§18), and the fallback to today's
* averaging when the views contradict each other. See qr-fused-pose.ts.md.
*/
const DEFAULTS = {
	...FUSED_WINDOW_DEFAULTS,
	minViews: 5,
	maxFitPx: 1.5,
	fallbackFitPx: 3,
	hysteresis: 1.5,
	sizeM: .16
};
/**
* How long ignored native frames may follow the window's newest entry
* before the stable pose is withdrawn, ms (plan §57 #1). Provisional: twice
* the corner-order chain's `memoryMs`; native frames begin where the chain
* ends, which is typical of a hand-held move.
*/
const NATIVE_HOLD_MS = 1e3;
const UNKNOWN = {
	status: "unknown",
	pose: null,
	method: null,
	views: 0,
	droppedViews: 0,
	fitPx: Infinity,
	windowEntries: 0,
	averagedRotationDeltaDeg: NaN,
	frameEpoch: 0,
	oldestTimestamp: NaN,
	newestTimestamp: NaN,
	motion: null,
	edgePx: null,
	notStableReason: "views",
	nativeIgnored: 0
};
/**
* The options with every numeric one checked: a missing, NaN or out-of-range
* value takes its default instead of silently switching a check off (an
* explicit `undefined` from a plain JS caller included).
*/
function resolveOptions(options) {
	const pick = (v, d, ok) => typeof v === "number" && ok(v) ? v : d;
	const positive = (x) => x > 0;
	return {
		...resolveFusedWindowOptions(options),
		minViews: pick(options.minViews, DEFAULTS.minViews, (x) => x >= 1),
		maxFitPx: pick(options.maxFitPx, DEFAULTS.maxFitPx, positive),
		fallbackFitPx: pick(options.fallbackFitPx, DEFAULTS.fallbackFitPx, positive),
		hysteresis: pick(options.hysteresis, DEFAULTS.hysteresis, (x) => x >= 1),
		sizeM: pick(options.sizeM, DEFAULTS.sizeM, (x) => positive(x) && Number.isFinite(x)),
		solveOptions: options.solveOptions,
		solve: options.solve
	};
}
/** The joint solve over the window and its robust fit statistic. */
function jointOf(window, o) {
	const views = window.map((e) => ({
		corners: e.corners,
		cameraPose: e.cameraPose,
		intrinsics: e.intrinsics
	}));
	const joint = (o.solve ?? solveQrPoseMultiView)(views, o.sizeM, o.solveOptions);
	return {
		joint,
		fitPx: joint && joint.viewRmsPx.length ? interpolatingMedian(joint.viewRmsPx) : Infinity
	};
}
/** Whether a state is (still) held: under the bound, or under bound x hysteresis if held before. */
function held(value, bound, before, hyst) {
	return value <= bound || before && value <= bound * hyst;
}
/**
* The fused pose of the window selected from `entries`. `previous` (the last
* result for the same code) makes the gate and the method sticky, so the
* output does not flicker between states or methods - but only while the
* window CONTINUES it (same frame epoch, no gap above `gapMs` between the
* previous window's newest entry and this window's oldest).
*/
function evaluateFusedQrPose(entries, options = {}, previous) {
	const o = resolveOptions(options);
	const kept = ignoreNativeWhenOrdered(entries, o.gapMs);
	const nativeIgnored = entries.length - kept.length;
	const window = selectFusedWindow(kept, o);
	const oldest = window[0];
	const newest = window[window.length - 1];
	if (!oldest || !newest) return {
		...UNKNOWN,
		nativeIgnored
	};
	const averaged = averagedOf(window);
	const { joint, fitPx } = jointOf(window, o);
	const frameEpoch = newest.frameEpoch ?? 0;
	const { useJoint, stable, reason } = gate(decide(joint, fitPx, o, continues(previous, frameEpoch, oldest, newest, o.gapMs) ? previous : null), joint, o, heldTooLong(entries, kept, newest));
	return {
		status: stable ? "stable" : "measuring",
		...poseOf(useJoint ? joint : null, averaged, joint),
		views: joint ? joint.views : 0,
		droppedViews: joint ? joint.droppedViews : 0,
		fitPx,
		windowEntries: window.length,
		averagedRotationDeltaDeg: rotationDeltaDeg(joint, averaged),
		frameEpoch,
		oldestTimestamp: oldest.timestamp,
		newestTimestamp: newest.timestamp,
		motion: null,
		edgePx: medianEdgePx(window),
		notStableReason: reason,
		nativeIgnored
	};
}
/** The gate's decision, withdrawn (`order`) after too long on ignored natives. */
function gate(decided, joint, o, withdrawn) {
	const stable = decided.stable && !withdrawn;
	return {
		useJoint: decided.useJoint,
		stable,
		reason: withdrawn ? "order" : notStableReason(joint, stable, decided.useJoint, o)
	};
}
/**
* The newest input entry is an ignored native one, more than
* {@link NATIVE_HOLD_MS} after the window's newest entry.
*/
function heldTooLong(entries, kept, windowNewest) {
	const newest = entries[entries.length - 1];
	return newest !== void 0 && newest !== kept[kept.length - 1] && newest.timestamp - windowNewest.timestamp > NATIVE_HOLD_MS;
}
function medianEdgePx(window) {
	const edges = [];
	for (const e of window) {
		const edge = meanEdgePx(e.corners);
		if (edge !== null) edges.push(edge);
	}
	return edges.length ? interpolatingMedian(edges) : null;
}
/** Which of the gate's conditions failed, in the order `decide` applies them. */
function notStableReason(joint, stable, useJoint, o) {
	if (stable) return null;
	if (!useJoint || !joint) return "fallback";
	return joint.views < o.minViews ? "views" : "fit";
}
/**
* Whether this window continues `previous`'s: same frame epoch, not older (a
* replay seek backwards starts afresh), and no gap above `gapMs` between the
* previous window's newest entry and this window's oldest.
*/
function continues(previous, frameEpoch, oldest, newest, gapMs) {
	if (!previous || previous.status === "unknown") return false;
	if (previous.frameEpoch !== frameEpoch) return false;
	if (!(newest.timestamp >= previous.newestTimestamp)) return false;
	return oldest.timestamp - previous.newestTimestamp <= gapMs;
}
/**
* The method and the gate: the joint rotation unless its fit is above the
* fallback bound; stable with enough views and a fit under the gate's bound.
* Both bounds stretch by `hysteresis` for a state held before.
*/
function decide(joint, fitPx, o, previous) {
	const wasJoint = previous ? previous.method === "joint" : false;
	const wasStable = previous ? previous.status === "stable" : false;
	const useJoint = joint !== null && held(fitPx, o.fallbackFitPx, wasJoint, o.hysteresis);
	return {
		useJoint,
		stable: useJoint && joint.views >= o.minViews && held(fitPx, o.maxFitPx, wasStable, o.hysteresis)
	};
}
/** Today's aggregate of the window's raw poses (median position, averaged rotation). */
function averagedOf(window) {
	const raws = [];
	for (const e of window) if (e.rawPose) raws.push(e.rawPose);
	const agg = aggregateQrPose(raws);
	return agg ? agg.pose : null;
}
/**
* The output pose: the joint rotation when `chosen` is set, else the
* averaged one; the position from the raw poses (today's median) when there
* are any, else the joint solve's.
*/
function poseOf(chosen, averaged, joint) {
	const rotation = chosen ? chosen.rotation : averaged && averaged.rotation;
	const position = averaged ? averaged.position : joint && joint.position;
	if (!rotation || !position) return {
		pose: null,
		method: null
	};
	return {
		pose: {
			rotation,
			position
		},
		method: chosen ? "joint" : "averaged"
	};
}
function rotationDeltaDeg(joint, averaged) {
	if (!joint || !averaged) return NaN;
	return geodesicAngleRad(joint.rotation, averaged.rotation) * 180 / Math.PI;
}
/**
* The window cut the motion detector asks for (plan §26): while the code is
* moved or turned only the newest entry (never stable, so the app shows the
* raw pose), once it is still again only the entries since then.
*/
function motionCutMs(motion, entries) {
	if (!motion) return void 0;
	if (motion.moving || motion.turning) return entries[entries.length - 1]?.timestamp;
	return motion.stillSinceMs ?? void 0;
}
/**
* A per-code tracker: caches on the entries ARRAY identity (the store hands
* out a new array per new detection; never a timestamp, which replay and
* store swaps reuse), carries the previous result for the hysteresis, and
* runs the motion detector (unless `motion: false`), which cuts the window
* once a motion is CONFIRMED, so the views from before it stop being fused.
* A motion too short or too slow to be confirmed is not cut (see the
* sidecar).
*/
function createFusedQrPoseTracker(options = {}) {
	const motionTracker = options.motion === false ? null : createQrMotionTracker({
		...options.sizeM === void 0 ? {} : { sizeM: options.sizeM },
		...options.gapMs === void 0 ? {} : { gapMs: options.gapMs },
		...options.motion
	});
	const windowGapMs = resolveFusedWindowOptions(options).gapMs;
	let lastEntries = null;
	let last = null;
	return {
		evaluate(entries) {
			if (entries === lastEntries && last) return last;
			const motion = motionTracker ? motionTracker.update(entries) : null;
			const sinceMs = motionCutMs(motion, ignoreNativeWhenOrdered(entries, windowGapMs));
			const fused = evaluateFusedQrPose(entries, sinceMs === void 0 ? options : {
				...options,
				sinceMs
			}, last);
			const cutByMotion = motion !== null && (motion.moving || motion.turning);
			last = {
				...fused,
				motion,
				notStableReason: cutByMotion && fused.status !== "stable" ? "motion" : fused.notStableReason
			};
			lastEntries = entries;
			return last;
		},
		reset() {
			motionTracker?.reset();
			lastEntries = null;
			last = null;
		}
	};
}
//#endregion
export { selectFusedWindow as a, measureQrMotion as i, evaluateFusedQrPose as n, solveQrPoseMultiView as o, createQrMotionTracker as r, viewErrorAtRotationPx as s, createFusedQrPoseTracker as t };
